
import React from "react";
import { Button } from "@/components/ui/button";
import { FileText, ExternalLink, Download, Play, Sparkles } from "lucide-react";
import { useProductDocuments } from "@/hooks/useProductDocuments";
import { Link } from "react-router-dom";

interface PriceData {
  productId: string;
  productName: string;
  price: string;
  msrp: string;
  [key: string]: string;
}

interface ProductButtonsProps {
  id: string;
  priceData: PriceData | null;
  onShowPrice: () => void;
}

export const ProductButtons: React.FC<ProductButtonsProps> = ({ 
  id, 
  priceData, 
  onShowPrice 
}) => {
  const { getDocumentLink } = useProductDocuments(id);
  
  const documentLink = getDocumentLink();
  
  // Special download link for RXT-8D
  const isRXT8D = id === "rxt-8d";
  const downloadLink = "https://auroramultimedia.com/products/rxt-8-d?section=download";
  
  // Add link for VPX product overview video
  const isVPX = id === "vpx-tc1-lt" || id === "vpx-tc1-pro";
  const videoOverviewLink = "https://e.pcloud.link/publink/show?code=XZhdudZA6LaDG5523jFg0qedquotLhBUxe7";

  // DIDO X4 checks - make sure we catch both possible ID formats
  const isDIDOX4 = id === "DIDO-X4" || id === "dido-x4";
  const didoX4Link = "https://auroramultimedia.com/products/dido-x4?section=download";
  const didoX4VideoLink = "https://e.pcloud.link/publink/show?code=XZHaBdZ63jp0mXOQRm26tFR4GoD7znsmuHk";

  // Add link for CW-1
  const isCW1 = id.toLowerCase() === "cw-1";
  const cw1Link = "https://auroramultimedia.com/products/cw-1?section=download";

  console.log("ProductButtons - Current product ID:", id);
  console.log("ProductButtons - isDIDOX4 check:", isDIDOX4);
  console.log("ProductButtons - About to render buttons");

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {/* DIDO X4 Features & Benefits Button - FIRST */}
      {isDIDOX4 && (
        <Link to="/features-benefits-dido">
          <Button 
            className="w-full sm:w-auto bg-[#069E50] text-white hover:bg-[#069E50]/90"
            style={{ backgroundColor: '#069E50' }}
          >
            <Sparkles className="mr-2" />
            DIDO X4 - Features & Benefits
          </Button>
        </Link>
      )}

      {documentLink && !isDIDOX4 && !isCW1 && (
        <a 
          href={documentLink} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Button 
            variant="outline" 
            className="w-full sm:w-auto"
          >
            <FileText className="mr-2" />
            Documents & Downloads
            <ExternalLink className="ml-2 h-3 w-3" />
          </Button>
        </a>
      )}
      
      {isDIDOX4 && (
        <a 
          href={didoX4Link} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Button 
            variant="outline" 
            className="w-full sm:w-auto"
          >
            <FileText className="mr-2" />
            Documents & Downloads
            <ExternalLink className="ml-2 h-3 w-3" />
          </Button>
        </a>
      )}
      
      {isDIDOX4 && (
        <a 
          href={didoX4VideoLink} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Button 
            variant="secondary" 
            className="w-full sm:w-auto"
          >
            <Play className="mr-2" />
            View Product Overview Video
            <ExternalLink className="ml-2 h-3 w-3" />
          </Button>
        </a>
      )}

      {isCW1 && (
        <a 
          href={cw1Link} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Button 
            variant="outline" 
            className="w-full sm:w-auto"
          >
            <FileText className="mr-2" />
            Documents & Downloads
            <ExternalLink className="ml-2 h-3 w-3" />
          </Button>
        </a>
      )}
      
      {isRXT8D && (
        <a 
          href={downloadLink} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Button 
            variant="secondary" 
            className="w-full sm:w-auto"
          >
            <Download className="mr-2" />
            Documents & Downloads
            <ExternalLink className="ml-2 h-3 w-3" />
          </Button>
        </a>
      )}
      
      {isVPX && (
        <a 
          href={videoOverviewLink} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Button 
            variant="secondary" 
            className="w-full sm:w-auto"
          >
            <Play className="mr-2" />
            View Product Overview Video
            <ExternalLink className="ml-2 h-3 w-3" />
          </Button>
        </a>
      )}
    </div>
  );
};
